import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, collection, onSnapshot, query, where, updateDoc, serverTimestamp, getDoc, addDoc } from 'firebase/firestore';
import { Card, Button } from '../components/ui';

const PaymentSuccess = () => {
  const { userProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('Verifying Payment...');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showManualButton, setShowManualButton] = useState(false);
  const processedRef = useRef(false);

  const activateAccount = async () => {
    if (!userProfile) {
      setStatus("Waiting for authentication...");
      return;
    }
    if (isProcessing) return;

    setIsProcessing(true);
    setError('');

    try {
      const userRef = doc(db, 'users', userProfile.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = userSnap.data();
        if (data.isPaid === true && data.subscriptionStatus === 'active') {
          setStatus("Payment already verified. Taking you to roadmap...");
          await refreshProfile();
          setTimeout(() => navigate('/dashboard'), 1000);
          return;
        }
      }

      setStatus("Unlocking Roadmap Access...");
      const pendingTier = localStorage.getItem('pending_tier') || 'tier_1';

      await updateDoc(userRef, {
        subscriptionStatus: 'active',
        isPaid: true,
        planTier: pendingTier,
        paymentMethod: 'stripe',
        lastUpdated: serverTimestamp()
      });

      localStorage.removeItem('pending_tier');

      try {
        await addDoc(collection(db, 'mail'), {
          to: [userProfile.email],
          message: {
            subject: 'Roadmap Unlocked - Welcome to DAD HUB',
            text: `Hi ${userProfile.displayName}, your training modules are now active.`,
            html: `<div style="padding: 20px; font-family: sans-serif;"><h2 style="color: #4f46e5;">Access Granted ✅</h2><p>You now have full access to the training roadmap.</p><a href="${window.location.origin}/#/dashboard">Go to Dashboard</a></div>`
          }
        });
      } catch (e) {
        // ignore mail error
      }

      setStatus("Success! Loading your training...");
      await refreshProfile();
      setTimeout(() => navigate('/dashboard'), 1500);

    } catch (e: any) {
      console.error("Activation failed:", e);
      setError("Account update failed. This can happen due to network delays. Please click 'Verify Now' to try again.");
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!processedRef.current) setShowManualButton(true);
    }, 8000);

    if (userProfile && !processedRef.current) {
      processedRef.current = true;
      activateAccount();
    }

    return () => clearTimeout(timer);
  }, [userProfile]);

  const handleManualRetry = () => {
    processedRef.current = false;
    setIsProcessing(false);
    activateAccount();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md p-10 text-center shadow-2xl border-0 ring-1 ring-black/5">
        {error ? (
          <div className="text-rose-600 animate-fade-in">
            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold mb-2 text-slate-900">Update in Progress</h2>
            <p className="text-sm mb-8 text-slate-500 leading-relaxed">{error}</p>
            <Button onClick={handleManualRetry} className="w-full !py-4 shadow-xl shadow-rose-100">
              Verify Account Now
            </Button>
          </div>
        ) : (
          <div className="animate-fade-in">
            <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-8 relative">
              <div className="text-5xl animate-bounce">✅</div>
              <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin"></div>
            </div>
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">Payment Confirmed</h2>
            <p className="text-slate-500 mb-8 text-sm leading-relaxed">
              Your payment was successful. We are granting your account access now. Do not close this window.
            </p>

            <div className="bg-slate-100 rounded-full h-2 w-full overflow-hidden mb-6">
              <div className="h-full bg-indigo-600 animate-pulse w-full origin-left transition-all duration-1000"></div>
            </div>

            <p className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em]">{status}</p>

            {showManualButton && (
              <div className="mt-10 pt-6 border-t border-slate-100 animate-slide-up">
                <p className="text-xs text-slate-400 mb-4 font-medium italic">
                  Still waiting? Click the button below to force activation.
                </p>
                <Button onClick={handleManualRetry} variant="outline" className="w-full">
                  Manual Activation
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default PaymentSuccess;
